import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type { WorkspaceRow } from '@/lib/types/workspace.types';

/**
 * Row data + selection for the Workspace dynamic table (per
 * WORKSPACE_IMPLEMENTATION_PLAN.md section 4). Rows are loaded from
 * GET /api/workspace/rows/:userUploadId (RTK Query, workspaceApi) and mirrored
 * here so inline cell edits have somewhere to live locally — the Workspace
 * API has no per-cell write endpoint yet, so `updateCell` is optimistic/local
 * only. Re-running the rows query (refetch/pagination) re-fetches this page's
 * rows from the server, so locally-edited cells are re-applied on top of the
 * fresh data (see `editedCells`) rather than being silently lost.
 */

interface WorkspaceDataState {
  byId: Record<string, WorkspaceRow>;
  allIds: string[];
  selectedRowIds: string[];
  /** `${rowId}:${key}` -> locally-edited value, replayed onto incoming rows
   * in setRows since a refetch (pagination/manual refresh/post-enrichment)
   * would otherwise overwrite unpersisted edits with the server's stale copy. */
  editedCells: Record<string, string>;
}

const initialState: WorkspaceDataState = {
  byId: {},
  allIds: [],
  selectedRowIds: [],
  editedCells: {},
};

const workspaceDataSlice = createSlice({
  name: 'workspaceData',
  initialState,
  reducers: {
    setRows(state, action: PayloadAction<WorkspaceRow[]>) {
      // Local edits grouped by row, so each incoming row is copied at most once.
      const editsByRow: Record<string, Record<string, string>> = {};
      for (const [cellId, value] of Object.entries(state.editedCells)) {
        const [rowId, key] = cellId.split(':');
        (editsByRow[rowId] ??= {})[key] = value;
      }
      state.byId = {};
      state.allIds = [];
      for (const row of action.payload) {
        // The payload rows are RTK Query's frozen cache objects — assigning
        // into one threw "Cannot assign to read only property 'input'", so
        // edits go onto a copy instead.
        const edits = editsByRow[row._id];
        state.byId[row._id] = edits ? { ...row, input: { ...row.input, ...edits } } : row;
        state.allIds.push(row._id);
      }
      // Drop selections for rows no longer on this page.
      state.selectedRowIds = state.selectedRowIds.filter((id) => state.byId[id]);
    },
    /** Local-only edit — not yet persisted upstream (no cell-write endpoint exists). */
    updateCell(state, action: PayloadAction<{ rowId: string; key: string; value: string }>) {
      const { rowId, key, value } = action.payload;
      const row = state.byId[rowId];
      if (!row) return;
      row.input = { ...row.input, [key]: value };
      state.editedCells[`${rowId}:${key}`] = value;
    },
    toggleRowSelection(state, action: PayloadAction<string>) {
      const id = action.payload;
      state.selectedRowIds = state.selectedRowIds.includes(id)
        ? state.selectedRowIds.filter((rowId) => rowId !== id)
        : [...state.selectedRowIds, id];
    },
    setSelectedRowIds(state, action: PayloadAction<string[]>) {
      state.selectedRowIds = action.payload;
    },
    clearSelection(state) {
      state.selectedRowIds = [];
    },
    /** Full reset for a sheet switch — clears rows, selection, and any
     * unpersisted local edits so they don't leak into the next sheet. */
    resetData(state) {
      state.byId = {};
      state.allIds = [];
      state.selectedRowIds = [];
      state.editedCells = {};
    },
  },
});

export const { setRows, updateCell, toggleRowSelection, setSelectedRowIds, clearSelection, resetData } =
  workspaceDataSlice.actions;

/**
 * Memoized with createSelector — a plain `.map().filter()` here would return
 * a new array on every call, which broke a useEffect keyed on this selector's
 * result (search-match recompute) into an infinite dispatch loop, since the
 * "changed" dependency never stabilized. This only recomputes when byId/
 * allIds actually change.
 */
export const selectWorkspaceRows = createSelector(
  [(state: RootState) => state.workspaceData.byId, (state: RootState) => state.workspaceData.allIds],
  (byId, allIds): WorkspaceRow[] => allIds.map((id: string) => byId[id]).filter(Boolean)
);

export const selectWorkspaceSelectedRowIds = (state: RootState): string[] => state.workspaceData.selectedRowIds;

export default workspaceDataSlice.reducer;
